import { createFileRoute } from "@tanstack/react-router";
import { IconBriefcase } from "@offsilo/ui/icon";
import { ScrollRegion } from "@offsilo/ui/scroll";
import { SkeletonRows } from "@offsilo/ui/skeleton";
import { EmptyState } from "@offsilo/ui/state";
import { tourAnchor } from "@offsilo/ui/tour-anchor";
import { usePageChrome } from "@/app/chrome";
import { PageState } from "@/app/page-state";
import { ChangesPanel } from "@/features/changes/ChangesPanel";
import { LaunchChecklists } from "@/features/inventory/InventoryPanel";
import { ScopePanel } from "@/features/scope/ScopePanel";
import { useCollection } from "@/data/hooks";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/_app/projects/$project/scope")({ component: Page });

/** One project's scope: the SOW baseline with its revision rounds, the change requests, and the launch checklist. */
function Page() {
  const { project: slug } = Route.useParams();
  const projects = useCollection("projects");
  const found = projects.data?.find((p) => p.slug === slug);
  usePageChrome([{ id: "projects", label: m.projscope_crumb() }, { id: slug, label: found?.name ?? slug }]);

  return (
    <ScrollRegion name="projscope" memoryKey={`projscope:${slug}`} aria-label={m.projscope_label()} gutter className="px-7 pb-6 max-tablet:px-4">
      <PageState
        query={projects}
        skeleton={<SkeletonRows rows={4} className="rounded-xl bg-card shadow-card" />}
        isEmpty={() => !found}
        empty={<EmptyState icon={IconBriefcase} title={m.projscope_missing_title()} body={m.projscope_missing_body()} />}
      >
        {() =>
          found ? (
            <div className="flex max-w-4xl flex-col gap-12">
              <h1 {...tourAnchor("projects.scope.title")} className="font-title text-3xl leading-tight">{found.name}</h1>
              <ScopePanel projectId={found.id} currency={found.budget.currency} />
              <ChangesPanel projectId={found.id} currency={found.budget.currency} />
              <section {...tourAnchor("projects.scope.launch")} aria-label={m.projscope_launch()} className="flex flex-col gap-4">
                <h2 className="font-title text-2xl leading-tight">{m.projscope_launch()}</h2>
                <LaunchChecklists projectId={found.id} />
              </section>
            </div>
          ) : null
        }
      </PageState>
    </ScrollRegion>
  );
}
